import {
    Avatar,
    Button,
    Flex,
    Input,
    Popover,
    Spin,
    Table,
    TableProps,
    Tag,
    Tooltip,
    TreeSelect,
    Typography
} from "antd";
import { MdOutlinePhone } from "react-icons/md";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { formatPhoneNumber, phoneNumberValidate } from "../../utils/phoneLib";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { generateNameSpaceColor } from "../../utils/color";
import { Contact } from "@data-phone/react-generic/dist/components/Forms/Contacts";
import { IoSearch } from "react-icons/io5";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetContacts } from "../../hooks/apis/contacts.api";
import { useGetCategories } from "../../hooks/apis/categories.api";
import { useGetTagsByCategory } from "../../hooks/apis/tags.api";
import ModalWrapper from "../generic/modal";
import { SelectedContacts } from "../Contacts/SelectedContacts.list";
import { LoadingOutlined } from "@ant-design/icons";
import GooglePlaceInput from "../Google/AutoCompleteLocation";
import { getPlaceById } from "../../hooks/apis/Google/places.api";
import { formatContactName } from "../../utils/contacts";

const avatarStyle: React.CSSProperties = {
    verticalAlign: "middle",
    color: "black",
    fontWeight: "bold"
};

const inputSearchStyle: React.CSSProperties = {
    padding: "10px",
    borderRadius: "25px"
};

const searchIconStyle: React.CSSProperties = {
    height: "20px",
    width: "20px",
    color: "green",
    cursor: "pointer"
};

export const ContactsTable = ({ setSelectedContacts, selectedContacts }: { setSelectedContacts: Function; selectedContacts: string[] }) => {
    const { refetch: fetchContacts, isLoading: isLoadingContacts } = useGetContacts();
    const { refetch: fetchCategories, isLoading: isLoadingCategories } = useGetCategories();
    const { refetch: fetchTags, isLoading: isLoadingTags } = useGetTagsByCategory();

    const { data: tags } = useSelector((state: RootState) => state.tags);
    const { data: contacts } = useSelector((state: RootState) => state.contacts);
    const { data: categories } = useSelector((state: RootState) => state.categories);

    const [searchTextState, setSearchTextState] = useState("");
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [invalidContacts, setInvalidContacts] = useState<string[]>([]);
    const [invalidContactsModal, setInvalidContactsModal] = useState<boolean>(false);
    const [validContactsModal, setValidContactsModal] = useState<boolean>(false);
    const [isProcessingSelection, setIsProcessingSelection] = useState<boolean>(false);

    const [locationLoading, setLocationLoading] = useState<boolean>(false);

    const tagsMap = useMemo(() => {
        const map = new Map<string, string | null>();
        tags.forEach(tag => {
            if (tag.category) {
                map.set(tag.name, tag.category);
            }
        });
        return map;
    }, [tags]);

    //location
    const [radius, setRadius] = useState<string>("");
    const [selectedPoint, setSelectedPoint] = useState<{
        latitude: number;
        longitude: number;
    } | null>(null);

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRadius(e.target.value);
    };

    useEffect(() => {
        if (!selectedPoint) setRadius("");
    }, [selectedPoint]);

    const getDistanceInMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const toRadians = (angle: number) => (Math.PI / 180) * angle;

        const R = 3958.8;
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    const handleFilterGeolocation = (contact: any) => {
        if (!selectedPoint || Number(radius) <= 0) return false;

        if (contact.addresses.length == 0) return false;
        if (
            contact.addresses[0].coordinates == null ||
            contact.addresses[0].coordinates.x == null ||
            contact.addresses[0].coordinates.y == null
        )
            return false;

        const distance = getDistanceInMiles(
            selectedPoint.latitude,
            selectedPoint.longitude,
            contact.addresses[0].coordinates.x as number,
            contact.addresses[0].coordinates.y as number
        );
        return distance <= Number(radius);
    };

    useEffect(() => {
        const invalidContacts = selectedContacts.filter(contact => {
            const contactData = contacts.find(c => c.id === contact);
            if (contactData?.phones.length === 0 || contactData?.phones?.every(phone => !phoneNumberValidate(phone.phone_number))) {
                return true;
            }
        });
        setInvalidContacts(invalidContacts);
    }, [selectedContacts]);

    useEffect(() => {
        fetchCategories();
        fetchTags();
        if (contacts.length == 0) {
            fetchContacts();
        }
    }, []);

    const filteredContacts = useMemo(() => {
        return contacts
            .filter(contact => {
                if (searchTextState === "" && selectedTags.length === 0) return true;

                const matchesSearchText = formatContactName(contact)
                    .toLowerCase()
                    .includes(searchTextState.toLowerCase()) ||
                    (contact.emails && contact.emails.some(email => email.email.toLowerCase().includes(searchTextState.toLowerCase()))) ||
                    (contact.phones && contact.phones.some(phone => phone.phone_number.includes(searchTextState)));

                const filteredTags = selectedTags.map(tag => {
                    const tagData = tagsMap.get(tag);
                    return {
                        name: tag,
                        category: tagData
                    };
                });

                const groupedTags: {
                    category: string | null;
                    tags: string[];
                }[] = Object.values(
                    filteredTags.reduce((acc, item) => {
                        const key = item.category || null;
                        if (!acc[key as string]) {
                            acc[key as string] = { category: key, tags: [] };
                        }
                        acc[key as string].tags.push(item.name);
                        return acc;
                    }, {} as Record<string, { category: string | null; tags: string[] }>)
                );

                const contactTagNames = contact.tags?.map(t => t.tag) || [];

                const categorizedGroups = groupedTags.filter(g => g.category !== null && g.tags.length > 0);
                const uncategorizedTags = groupedTags.find(g => g.category === null)?.tags || [];

                const matchesCategorized =
                    categorizedGroups.length === 0 ||
                    categorizedGroups.every(group => group.tags.some(tag => contactTagNames.includes(tag)));

                const matchesUncategorized = uncategorizedTags.length === 0 || uncategorizedTags.some(tag => contactTagNames.includes(tag));

                const matchesSelectedTags = 
                    (categorizedGroups.length > 0 ? matchesCategorized : true) &&
                    (uncategorizedTags.length > 0 ? matchesUncategorized : true);

                if (searchTextState && selectedTags.length) return matchesSearchText && matchesSelectedTags;
                if (searchTextState) return matchesSearchText;
                if (selectedTags.length > 0) return matchesSelectedTags;

                return false;
            })
            .filter(contact => {
                if (!selectedPoint || Number(radius) <= 0) return true;

                if (handleFilterGeolocation(contact)) return true;

                return false;
            })
            .map(contact => ({
                ...contact,
                key: contact.id
            }))
            .sort((a, b) => {
                return a.first_name?.localeCompare(b.first_name || "") || 0;
            });
    }, [contacts, searchTextState, selectedTags, selectedPoint, radius]);

    const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTextState(e.target.value);
    }, []);

    const renderNameColumn = (record: Contact) => {
        return (
            <Flex align="center" gap={10}>
                <Avatar style={{ ...avatarStyle, background: generateNameSpaceColor(record.first_name || "") }} size="large">
                    {record.first_name?.charAt(0).toUpperCase() || ""}
                </Avatar>
                <Flex vertical>
                    <Typography.Text style={{ fontWeight: "500" }}>
                        {formatContactName(record)}
                    </Typography.Text>

                    {record.emails && record.emails.length > 0 && (
                        <>
                            <Typography.Text type="secondary">{record.emails[0].email}</Typography.Text>
                            <Typography.Text type="secondary">{record.emails.length > 0 ? record.emails[0].email : ""}</Typography.Text>
                        </>
                    )}
                </Flex>
            </Flex>
        );
    };

    const renderPhoneColumn = (phones: Contact["phones"]) =>
        phones.length > 0 ? (
            <Flex gap="10px" align="center">
                <MdOutlinePhone color="blue" size={20} />
                <Typography.Text strong>{formatPhoneNumber(phones[0]?.phone_number)}</Typography.Text>
                {phones.length > 1 && (
                    <Popover
                        content={phones.map(phone => (
                            <Flex key={phone.phone_number} gap="10px" vertical>
                                <Typography.Text>{formatPhoneNumber(phone.phone_number)}</Typography.Text>
                            </Flex>
                        ))}
                    >
                        <HiOutlineDotsHorizontal cursor="pointer" />
                    </Popover>
                )}
            </Flex>
        ) : (
            <></>
        );

    const renderTagsColumn = (contactTags: Contact["tags"]) => (
        <Flex>
            {contactTags &&
                contactTags.length > 0 &&
                contactTags.length <= 3 &&
                contactTags
                    ?.slice(0, 3)
                    .sort((a, b) => a.tag.localeCompare(b.tag))
                    .map(tag => (
                        <Tag key={tag.tag} className="text-ellipsis" color="blue">
                            <Tooltip title={tags.find(t => t.name === tag.tag)?.name}>{tags.find(t => t.name === tag.tag)?.name}</Tooltip>
                        </Tag>
                    ))}
            {contactTags && contactTags.length > 3 && (
                <Popover
                    content={
                        contactTags &&
                        contactTags.length > 3 &&
                        [...contactTags]
                            ?.sort((a, b) => a.tag.localeCompare(b.tag))
                            ?.map(tag => (
                                <Tag key={tag.tag} color="blue">
                                    <Tooltip title={tags.find(t => t.name === tag.tag)?.name}>
                                        {tags.find(t => t.name === tag.tag)?.name}
                                    </Tooltip>
                                </Tag>
                            ))
                    }
                >
                    <Button style={{ height: "25px" }} icon={<HiOutlineDotsHorizontal />} />
                </Popover>
            )}
        </Flex>
    );

    const columns: TableProps<Contact>["columns"] = [
        {
            key: "name",
            title: "Name",
            render: (_, record) => renderNameColumn(record)
        },
        {
            key: "phone",
            title: "Phone",
            render: (_, record) => renderPhoneColumn(record.phones)
        },
        {
            key: "tags",
            title: "Tags",
            render: (_, record) => renderTagsColumn(record.tags)
        }
    ];

    const handlePlaceSelected = async (place: { place_id: string } | null) => {
        if (!place) {
            setSelectedPoint(null);
            return;
        }
        setLocationLoading(true);

        const placeDetails = await getPlaceById(place.place_id);

        if (!placeDetails || !placeDetails.result) return;

        if (
            placeDetails.result.geometry.location &&
            placeDetails.result.geometry.location.lat &&
            placeDetails.result.geometry.location.lng
        ) {
            setSelectedPoint({
                latitude: placeDetails.result.geometry.location.lat,
                longitude: placeDetails.result.geometry.location.lng
            });
        }

        setLocationLoading(false);
    };

    return (
        <Flex gap={10} vertical>
            <Input
                style={inputSearchStyle}
                onChange={handleSearchInput}
                size="middle"
                placeholder="Search"
                prefix={<IoSearch style={searchIconStyle} />}
            />
            <Flex gap={10} align="center">
                <TreeSelect
                    showSearch
                    style={{ width: "50%", borderRadius: "25px" }}
                    placeholder="Filter by tags"
                    dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                    allowClear
                    treeCheckable
                    loading={isLoadingCategories || isLoadingTags}
                    onChange={value => {
                        setSelectedTags(value);
                    }}
                    value={selectedTags}
                    treeData={[
                        ...categories,
                        {
                            name: "Uncategorized",
                            id: "uncategorized"
                        }
                    ].map(category => {
                        return {
                            title: category.name,
                            value: category.name,
                            key: category.name,
                            children: tags
                                .filter(tag => {
                                    if (category.name === "Uncategorized") {
                                        return tag.category === null;
                                    } else if (category.name !== null) {
                                        return tag.category === category.name;
                                    }
                                })
                                .map(tag => {
                                    return {
                                        title: tag.name,
                                        value: tag.name,
                                        key: tag.name
                                    };
                                })
                        };
                    })}
                />
                {<Tag color="green">Contacts: {filteredContacts.length}</Tag>}
                {selectedContacts.filter(contact => !invalidContacts.includes(contact)).length > 0 && (
                    <Tag onClick={() => setValidContactsModal(true)} style={{ cursor: "pointer" }} color="green">
                        Valid Contacts: {selectedContacts.filter(contact => !invalidContacts.includes(contact)).length}
                    </Tag>
                )}
                {invalidContacts.length > 0 && (
                    <Tag onClick={() => setInvalidContactsModal(true)} style={{ cursor: "pointer" }} color="red">
                        Invalid Contacts: {invalidContacts.length}
                    </Tag>
                )}
            </Flex>
            <Flex gap={10}>
                <GooglePlaceInput loading={locationLoading} onPlaceSelected={handlePlaceSelected} />
                <Input
                    style={{ width: "40%" }}
                    placeholder="Radius (miles)"
                    value={radius}
                    disabled={!selectedPoint}
                    onChange={e => {
                        if (e.target.value === "") {
                            handleRadiusChange(e);
                        } else {
                            const regex = /^[0-9]+$/;
                            if (regex.test(e.target.value)) {
                                handleRadiusChange(e);
                            }
                        }
                    }}
                />
            </Flex>
            {invalidContacts.length > 0 && invalidContactsModal && (
                <ModalWrapper isModalOpen={invalidContactsModal} setIsModalOpen={setInvalidContactsModal} title="Invalid Contacts">
                    <SelectedContacts
                        setIsModal={setInvalidContactsModal}
                        contacts={invalidContacts}
                        setSelectedContacts={setSelectedContacts}
                    />
                </ModalWrapper>
            )}
            {validContactsModal && selectedContacts.filter(contact => !invalidContacts.includes(contact)).length > 0 && (
                <ModalWrapper isModalOpen={validContactsModal} setIsModalOpen={setValidContactsModal} title="Valid Contacts">
                    <SelectedContacts
                        setIsModal={setValidContactsModal}
                        contacts={selectedContacts.filter(contact => !invalidContacts.includes(contact))}
                        setSelectedContacts={setSelectedContacts}
                    />
                </ModalWrapper>
            )}
            <div style={{ height: "400px", overflowY: "auto" }}>
                <Table<Contact>
                    sticky={{ offsetHeader: 0 }}
                    rowSelection={{
                        type: "checkbox",
                        columnTitle: isProcessingSelection ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : undefined,
                        onSelect: (selectedRow, selected) => {
                            if (selected) {
                                setSelectedContacts((prev: string[]) => {
                                    return [...prev, selectedRow.id as string];
                                });
                            } else {
                                setSelectedContacts((prev: string[]) => {
                                    return prev.filter(contact => contact !== selectedRow.id);
                                });
                            }
                        },
                        onSelectAll: selected => {
                            setIsProcessingSelection(true);
                            setTimeout(() => {
                                if (selected) {
                                    setSelectedContacts(filteredContacts.map(contact => contact.id as string));
                                } else {
                                    setSelectedContacts([]);
                                }
                                setIsProcessingSelection(false);
                            }, 0);
                        },
                        selectedRowKeys: selectedContacts,
                        preserveSelectedRowKeys: true
                    }}
                    pagination={{
                        position: ["bottomCenter"]
                    }}
                    loading={isLoadingContacts}
                    rowKey={contact => contact.id || ""}
                    className="contact-table"
                    bordered={false}
                    dataSource={filteredContacts}
                    columns={columns}
                />
            </div>
        </Flex>
    );
};
